import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/Hooks';
import { startImpersonation, stopImpersonation, toggleShowAll } from '../../../store/slices/ShadowUserSlice';
import { updateUser } from '../../../store/slices/UserSlice';
import UserService from '../../../services/UserService';
import Utils from '../../../utils/Utils';
import translator from '../../../theme/translator.json';

const SwitcherSellers: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, language } = useAppSelector((state) => state.user);
  const { originalUser, showAll } = useAppSelector((state) => state.shadowUser);
  const [sellers, setSellers] = useState<any[]>([]);
  const [mainUser, setMainUser] = useState<any>(null);

  useEffect(() => {
    // Définir l'utilisateur principal
    const principal = originalUser || user;
    if (principal) {
      setMainUser(principal);
    }
  }, [originalUser, user]);

  useEffect(() => {
    const loadSellers = async () => {
      if (!mainUser?.id) return;
      try {
        const sellersList = await UserService.getAllSellers(mainUser.id);
        setSellers(sellersList);
      } catch (error) {
        console.error(
          Utils.getTranslation(language, translator.components.switcherSellers.loadingError),
          error,
        );
      }
    };

    if (mainUser) {
      loadSellers();
    }
  }, [mainUser, language]);

  const handleSellerChange = (event: SelectChangeEvent<string>) => {
    const selectedSellerId = event.target.value;

    // Si on sélectionne l'utilisateur principal
    if (selectedSellerId === mainUser?.id) {
      console.log('selectedSeller', mainUser);
      dispatch(stopImpersonation());
      dispatch(updateUser(mainUser));
      navigate('/');
      return;
    }

    // Si on sélectionne un vendeur
    const selectedSeller = sellers.find((seller) => seller.id === selectedSellerId);
    if (selectedSeller) {
      console.log('selectedSeller', selectedSeller);

      const mainUserSettings = {
        pdfExportContactInformation: mainUser.pdfExportContactInformation ?? true,
        pdfExportAdditionalInformation: mainUser.pdfExportAdditionalInformation ?? true,
        pdfExportClientDescription: mainUser.pdfExportClientDescription ?? true,
        pdfExportFollowUpHistory: mainUser.pdfExportFollowUpHistory ?? true,
        language: mainUser.language ?? 'fr',
        hidePriority: mainUser.hidePriority ?? false,
        showAdressField: mainUser.showAdressField ?? false,
        showAllInBanqueContacts: mainUser.showAllInBanqueContacts ?? false,
        showVendeurSuivis: mainUser.showVendeurSuivis ?? false,
        showVisites: mainUser.showVisites ?? false,
      };

      const sellerWithParent = {
        ...selectedSeller,
        ...mainUserSettings, // We do not consider the seller's settings
        originalUser: mainUser,
      };

      dispatch(startImpersonation(sellerWithParent));
      dispatch(updateUser(sellerWithParent));
      navigate('/');
    }
  };

  const handleToggleView = () => {
    dispatch(toggleShowAll(!showAll));
    // When toggling the view, we always want to be on the main user's account
    if (mainUser) {
      dispatch(updateUser(mainUser));
    }
  };

  // Si on n'a pas encore chargé l'utilisateur principal, on n'affiche rien
  if (!mainUser) return null;

  const currentUserId = user?.id || '';

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#1976d2',
        color: 'white',
        padding: '4px 0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1200,
        height: '32px',
        fontSize: '0.875rem',
      }}
    >
      {!showAll ? (
        <>
          <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
            {Utils.getTranslation(language, translator.components.switcherSellers.activeAccount)}
          </Typography>
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                height: '24px',
                fontSize: 'inherit',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'white',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
              '& .MuiSelect-icon': {
                color: 'white',
                fontSize: '1.2rem',
              },
              '& .MuiSelect-select': {
                padding: '2px 32px 2px 8px',
              },
            }}
          >
            <Select
              value={currentUserId}
              onChange={handleSellerChange}
              displayEmpty
              sx={{ color: 'white' }}
            >
              {/* Option pour l'utilisateur principal */}
              <MenuItem value={mainUser.id}>
                {Utils.getTranslation(
                  language,
                  translator.components.switcherSellers.principalAccount,
                ).replace('{name}', mainUser.name)}
              </MenuItem>

              {/* Options pour les vendeurs */}
              {sellers.map((seller) => (
                <MenuItem key={seller.id} value={seller.id}>
                  {seller.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        <Typography variant="body2" sx={{ fontSize: 'inherit' }}>
          {Utils.getTranslation(language, translator.components.switcherSellers.globalViewActive)}
        </Typography>
      )}
      <Button
        variant="outlined"
        size="small"
        onClick={handleToggleView}
        sx={{
          color: 'white',
          height: '24px',
          borderColor: 'rgba(255, 255, 255, 0.5)',
          textTransform: 'none',
          fontSize: 'inherit',
          padding: '2px 8px',
          minWidth: 'auto',
          '&:hover': {
            borderColor: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        {showAll
          ? Utils.getTranslation(language, translator.components.switcherSellers.backToSimpleView)
          : Utils.getTranslation(
            language,
            translator.components.switcherSellers.activateGlobalView,
          )}
      </Button>
    </Box>
  );
};

export default SwitcherSellers;
